import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { IMAGES } from "@/data/content";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="pt-16 lg:pt-[72px] min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={IMAGES.templeCarved} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1021]/60 to-[#0B1021]" />
        <div className="absolute bottom-12 left-12 right-12">
          <span className="font-display text-3xl tracking-[0.18em] text-amber-50">INDIC</span>
          <p className="mt-4 font-serif text-2xl italic text-amber-100/80 max-w-sm leading-snug">“Reality is not beneath us, not behind us, and not merely useful to us. We are here. Begin there.”</p>
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-slate-400">Mahakali Tribunal</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-4xl font-semibold text-amber-50">{title}</h1>
          <p className="mt-2 text-slate-400">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <div className="mt-6 text-sm text-slate-400">{footer}</div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-sm border border-indic-card-border bg-indic-card px-4 py-3 text-amber-50 placeholder:text-slate-500 focus:border-indic-gold/60 focus:outline-none";
const btnCls = "w-full rounded-sm bg-indic-gold px-4 py-3 font-sans font-semibold text-indic-indigo hover:bg-indic-gold-soft transition-colors disabled:opacity-50";

export function LoginForm() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="login-email" />
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} data-testid="login-password" />
      {error && <p className="text-sm text-indic-terracotta" data-testid="login-error">{error}</p>}
      <button type="submit" disabled={loading} className={btnCls} data-testid="login-submit">{loading ? "Signing in…" : "Sign in"}</button>
    </form>
  );
}

export function RegisterForm() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Welcome to INDIC");
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={inputCls} data-testid="register-name" />
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="register-email" />
      <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)" className={inputCls} data-testid="register-password" />
      {error && <p className="text-sm text-indic-terracotta" data-testid="register-error">{error}</p>}
      <button type="submit" disabled={loading} className={btnCls} data-testid="register-submit">{loading ? "Creating account…" : "Create account"}</button>
    </form>
  );
}

export { AuthShell };
